// Shared helpers for the org tag vocabulary (key -> value). Used by the Tag Management page and the
// dashboards' Group By control so the palette, reserved-key list, and chip contrast stay in one place.

// Fixed chip palette (mirrors FILTER_COLOR_OPTIONS; white dropped — invisible on white chips).
export const TAG_COLOR_OPTIONS = ['#00AB55', '#000000', '#FFC0CB', '#FF4842', '#1890FF', '#94D82D', '#FFC107', '#7A0C2E'];

// Group tags (key 'asset-group'/'shipment-group') are DB-owned and surface as assetGroupId/Name on the
// dashboard — they are managed via the groups UI and hidden from the user-facing tag vocabulary.
export const RESERVED_TAG_KEYS = new Set(['asset-group', 'shipment-group']);

export const isReservedKey = (key) => RESERVED_TAG_KEYS.has(String(key || '').toLowerCase());

// Readable text color for a colored chip (luminance threshold) — keeps labels legible on light colors.
export const contrastText = (hex) => {
  const c = String(hex || '').replace('#', '');
  if (c.length !== 6) return '#fff';
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#212B36' : '#fff';
};

// Re-section already-filtered asset groups into synthetic groups keyed by one tag field's value —
// "group by City" => a Blr group, a Hyd group, and a "No City" group for assets without that field.
// Reuses the AssetGroup section shape (id/name/assets/aggCountByType) with synthetic:true so the
// dashboard can render them as read-only sections. aggCountByType is recomputed per bucket for badges.
export const regroupByField = (assetGroups, field, tagById, summaryBy, info = '') => {
  const noLabel = `No ${field}`;
  // Synthetic groups must carry the same asset/shipment `type` as the real groups, or AssetGroup crashes
  // on assetsConfig[type]. Real groups (and their assets) carry it; take it from the first available.
  const groupType = (assetGroups || [])[0]?.type || (assetGroups || [])[0]?.assets?.[0]?.type;
  const buckets = {};
  (assetGroups || []).forEach((ag) => {
    (ag.assets || []).forEach((a) => {
      const tid = (a.tagIds || []).find((id) => tagById[id]?.key === field);
      const label = tid ? tagById[tid].value : noLabel;
      if (!buckets[label]) {
        buckets[label] = {
          // The "No <field>" bucket reuses id 'default' so AssetGroup renders it borderless (like the
          // real ungrouped/default group) instead of as a bordered "empty-looking" section.
          id: label === noLabel ? 'default' : `tag:${field}:${label}`, name: label, field, type: groupType, assets: [], aggCountByType: {}, attachedDevices: 0, synthetic: true,
        };
      }
      buckets[label].assets.push(a);
      // Carry the attached-logger tally into the bucket. Pages stamp `attachedDevices` onto each asset
      // and each REAL group; a synthetic bucket has neither, so AssetGroup's chip fell through to
      // `Object.keys(assets?.devices || {}).length` — and `assets` there is an ARRAY, which has no
      // `.devices`, so any grouped view showed "Loggers Attached: 0". Summing the per-asset value fixes
      // it for single-level grouping and for every level of a tree alike.
      buckets[label].attachedDevices += a.attachedDevices || 0;
    });
  });
  return Object.keys(buckets)
    // Bucket labels are usually numbered ("Floor 2", "Floor 10"), which a plain localeCompare orders
    // wrongly ("Floor 10" before "Floor 2"). Numeric collation fixes it natively, no dependency. It
    // belongs HERE, in the one comparator every level runs through, rather than being re-sorted on top
    // by the multi-level builder — that made a field's order depend on how many levels were selected.
    .sort((x, y) => {
      if (x === noLabel) return 1;
      if (y === noLabel) return -1;
      return x.localeCompare(y, undefined, { numeric: true, sensitivity: 'base' });
    })
    .map((label) => {
      const b = buckets[label];
      b.assets.forEach((a) => Object.entries((summaryBy && a[summaryBy]) || {}).forEach(([type, vw]) => {
        // Mirror the ungrouped info-active tally: when an info filter is on, count only that type so
        // grouped badge counts match the ungrouped view instead of carrying spurious other-type counts.
        if (info && type !== info) return;
        b.aggCountByType[type] = (b.aggCountByType[type] || 0) + Number(!!vw.n);
      }));
      return b;
    });
};

// Multi-level ("leveling") grouping ------------------------------------------------------------
// A LEVEL is a tag key; a hierarchy is an ORDERED list of keys (Country ▸ State ▸ City ▸ Branch ▸ Room).
// The cap lives in one constant so raising it is a one-line change rather than a hunt — the picker's
// limit, its "n/N" counter, the write-side cap and the depth-rail ramp all read it.
export const MAX_GROUP_LEVELS = 5;

// One level of the tree. Buckets via regroupByField so EVERY level buckets, labels, counts, ORDERS and
// handles its "No <field>" case identically to the shipped single-level grouping, then recurses into
// each bucket for the next key. Purely functional — nodes are fresh objects and the asset references
// inside them are never mutated (pages share those references with live socket state).
const buildLevel = (groups, keys, depth, parentPath, tagById, summaryBy, info) => {
  const field = keys[depth];
  const isLast = depth === keys.length - 1;
  return regroupByField(groups, field, tagById, summaryBy, info)
    .map((bucket) => {
      // Bucket ids repeat across branches ('default' for every "No <field>", and tag:Floor:Floor 1 under
      // every branch), so the PATH — not the id — is the React key and the expand key. The id is left
      // untouched so AssetGroup keeps rendering the 'default' bucket borderless.
      // '/' delimits path segments, so escape any inside a field or value — otherwise a value like
      // "A/B" would look like an ancestor of another branch and the accordion would open the wrong node.
      const segment = `${field}=${bucket.name}`.replace(/\//g, '%2F');
      const path = `${parentPath}${parentPath ? '/' : ''}${segment}`;
      // regroupByField gives the "No <field>" bucket id 'default', which makes AssetGroup render it
      // borderless AND nameless (right for the real ungrouped group at the top). Nested inside a tree
      // that surfaces as a blank row, so below the first level give it a real id and let its name show.
      const id = bucket.id === 'default' && depth > 0 ? `tag:${field}:${bucket.name}` : bucket.id;
      const node = {
        ...bucket, id, path, depth,
      };
      if (!isLast) node.children = buildLevel([bucket], keys, depth + 1, path, tagById, summaryBy, info);
      return node;
    });
};

// Re-section asset groups into a nested tree keyed by an ORDERED list of 2+ tag keys, whose nodes carry
// `children`, `path` and `depth` on top of regroupByField's bucket shape. Fewer than 2 keys is not a
// case this handles: the caller (useTagFilter's `displayGroups`) only reaches it behind `isLeveled`,
// which requires 2+, and routes 1 key straight to regroupByField and 0 to the real groups.
export const regroupByLevels = (assetGroups, levels, tagById, summaryBy, info = '') => (
  buildLevel(assetGroups, (levels || []).filter(Boolean), 0, '', tagById, summaryBy, info)
);

// Strength of a level's accent rail, as an alpha over the theme's primary colour. Level 1 is full
// strength and each level below fades, so how deep you are is readable from the bar alone. Alpha over
// `primary.main` (not a fixed hex) keeps it correct for whatever palette an org's theme uses.
//
// The fade STEP is derived from MAX_GROUP_LEVELS rather than hardcoded, so the deepest level always
// lands exactly on the floor and every level in between gets its own shade. With a fixed step, raising
// the cap silently gave the last two levels an identical bar — the one thing the rail exists to avoid.
const RAIL_MIN_ALPHA = 0.3;
export const levelRailAlpha = (depth) => Math.max(
  1 - (Number(depth) || 0) * ((1 - RAIL_MIN_ALPHA) / Math.max(MAX_GROUP_LEVELS - 1, 1)),
  RAIL_MIN_ALPHA,
);

// Every node path in a level tree, depth-first — used to seed "expand all".
export const collectNodePaths = (nodes) => (nodes || []).flatMap((n) => [n.path, ...collectNodePaths(n.children)]);

// What survives in the open set when a row is opened — the one rule both toggles below share.
//
// ONLY THE TOP LEVEL is an accordion. Opening a top-level row is navigation: you are choosing which
// area of the hospital to look at, and leaving the others open would grow the page without bound (one
// KIMS bucket alone holds 424 assets). Below that, siblings stay open, because reading Floor 2, Floor 4
// and Floor 11 next to each other is the entire point of a hierarchy — the old rule closed siblings at
// every depth, so drilling into one floor slammed the previous one shut.
//
// Depth is read off the path: segments are joined with '/' and any '/' inside a field or value is
// escaped, so a top-level path is exactly one with no separator in it. Ancestors are kept either way —
// a plain single-open rule would collapse the parent you just drilled through.
const keepWhenOpening = (list, path) => (
  path.includes('/') ? list : list.filter((p) => path.startsWith(`${p}/`))
);

// Open/close one row. Closing a row also closes everything beneath it.
export const toggleTreePath = (openPaths, path) => {
  const list = openPaths || [];
  if (list.includes(path)) return list.filter((p) => p !== path && !p.startsWith(`${path}/`));
  return [...keepWhenOpening(list, path), path];
};

// "Expand everything below this row": the row itself, every folder under it, and every leaf group, in
// one click — otherwise a 4-level hierarchy takes a click per node to open a single branch. Clicking it
// again closes exactly what it opened. Follows the same top-level accordion rule, so a branch opens the
// same way whether you got there by this button or by clicking the row.
export const toggleSubtreePaths = (openPaths, node) => {
  const subtree = collectNodePaths([node]);
  const list = openPaths || [];
  if (subtree.every((p) => list.includes(p))) return list.filter((p) => !subtree.includes(p));
  return [...new Set([...keepWhenOpening(list, node.path), ...subtree])];
};

// Remembering the Group By hierarchy ----------------------------------------------------------
// Picking an ordered set of fields in a dialog is not a passing question like "show me battery alerts
// only" — it is the shape someone wants their dashboard in, so it survives a refresh and moving
// between pages. It does NOT outlive the app: sessionStorage dies with the tab, and signing out clears
// it explicitly (sign-out removes auth keys one by one rather than clearing storage, so nothing else
// would drop it). One person's choice can never reach another's screen.
//
// Keyed per ORG and per DASHBOARD: assets and shipments want different hierarchies, and org-scoping
// stops one org's fields being restored under the next one after an org switch.
const GROUPING_KEY_PREFIX = 'tagLevels:';

export const groupingStorageKey = (orgId, scope) => `${GROUPING_KEY_PREFIX}${String(orgId)}:${String(scope || 'asset')}`;

export const readRememberedLevels = (key) => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
    return Array.isArray(saved) ? saved.filter((f) => typeof f === 'string' && f) : [];
  } catch { return []; } // unreadable or unparseable: fall back to no grouping
};

export const writeRememberedLevels = (key, levels) => {
  try {
    // Clearing the hierarchy clears the memory of it too, rather than leaving it to reappear later.
    if (levels?.length) sessionStorage.setItem(key, JSON.stringify(levels));
    else sessionStorage.removeItem(key);
  } catch { /* storage full or blocked: grouping still works, it just won't be remembered */ }
};

// Called on sign-out so the next person to use this browser starts with a plain dashboard.
export const clearRememberedGroupings = () => {
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith(GROUPING_KEY_PREFIX))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch { /* storage unavailable */ }
};

// Bucket a flat tag list into { key: Tag[] }, excluding reserved keys, each bucket sorted by value.
export const groupTagsByKey = (tags) => {
  const groups = {};
  (tags || []).forEach((t) => {
    if (isReservedKey(t.key)) return;
    (groups[t.key] = groups[t.key] || []).push(t);
  });
  Object.values(groups).forEach((arr) => arr.sort((a, b) => a.value.localeCompare(b.value)));
  return groups;
};
